import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
  FormBuilder,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Constants } from 'src/app/app.constants';

import { RewardService } from '../../../services/index';
import { CardType, Reward } from '../../../models/index';

@Component({
  selector: 'reward-dialog',
  templateUrl: './reward-dialog.component.html',
  styleUrls: ['./reward-dialog.component.css'],
})
export class RewardDialogComponent implements OnInit {
  form!: FormGroup;
  rewardFormControl!: FormControl;
  numberFormControl!: FormControl;
  pointsFormControl!: FormControl;

  rewards: Reward[] = [];
  loading = false;
  error = false;

  pageIndex = 0;
  pageSize = 10;
  length = 0;

  constructor(
    public matDialogRef: MatDialogRef<RewardDialogComponent>,
    public fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private rewardService: RewardService,
    private constants: Constants
  ) {}

  ngOnInit(): void {
    this.rewardFormControl = new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]);

    this.pointsFormControl = new FormControl({
      value: this.data?.maxPoints ?? 0,
      disabled: this.data?.cardType === CardType.point,
    });
    this.numberFormControl = new FormControl({ value: '', disabled: false });

    if (this.data?.stampNumbers && this.data?.cardType === CardType.stamp) {
      this.numberFormControl.setValidators([Validators.required]);
    }

    if (this.data?.cardType === CardType.point) {
      this.pointsFormControl.setValidators([Validators.required]);
    }

    this.form = this.fb.group({
      reward: this.rewardFormControl,
      number: this.numberFormControl,
      points: this.pointsFormControl,
    });

    this.getRewards();
  }

  getRewards(): void {
    this.loading = true;
    this.rewardService
      .getRewards(1000, 0)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.length = res.totalDocs;
            this.rewards = res.docs;
            this.rewards?.sort((a, b) => a.title!.localeCompare(b.title!));

            // If editing existing reward, pre-populate form after rewards are loaded
            if (this.data?.existingReward) {
              const existingReward = this.data.existingReward;

              // Find the reward in the loaded rewards array
              const rewardToSelect = this.rewards.find(
                (r) => r._id === existingReward.reward._id
              );
              if (rewardToSelect) {
                this.rewardFormControl.setValue(rewardToSelect);
              }

              // Set stamp number if it exists
              if (existingReward.stamp && this.data?.stampNumbers) {
                const stampToSelect = this.data.stampNumbers.find(
                  (s: any) => s.number === existingReward.stamp
                );
                if (stampToSelect) {
                  this.numberFormControl.setValue(stampToSelect);
                }
              }

              // Set points if they exist
              if (existingReward.points) {
                this.pointsFormControl.setValue(existingReward.points);
              }
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  primaryTapped() {
    if (this.form?.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const data: { reward: any; stamp?: any; points?: any } = {
      reward: this.rewardFormControl.value,
    };

    if (this.numberFormControl.value?.number) {
      data.stamp = this.numberFormControl.value.number;
    }

    if (
      this.pointsFormControl &&
      this.pointsFormControl.value !== null &&
      this.pointsFormControl.value !== undefined
    ) {
      data.points = this.pointsFormControl.value;
    }

    this.matDialogRef.close(data);
  }

  hasRewards(): void {
    if (!this.rewards.length) {
      this.constants.snack('Please add a reward first');
    }
  }

  get isEditing(): boolean {
    return !!this.data?.existingReward;
  }

  get dialogTitle(): string {
    return this.isEditing ? 'Edit Reward' : 'Select Reward';
  }
}
